import { Icon, Tag, TextField, Tooltip, Typography } from '@scale/scaleui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ZodFirstPartyTypeKind, ZodUnion } from 'zod';

interface FieldProps<T> {
  label: string;
  schema: T;
  value: any;
  onChange: (value: any) => void;
  optional?: boolean;
  disabled?: boolean;
}

const MaybeTooltip: React.FC<{ text?: string }> = ({ text }) => {
  if (text) {
    return (
      <Tooltip
        target={
          <div>
            <Icon icon="info-circle" size="sm" />
          </div>
        }
        text={<div className="max-w-[360px]">{text}</div>}
      />
    );
  }
  return null;
};

/**
 * AutoCompleteEnumBuilder - A component that provides autocomplete
 * suggestions for z.union([z.enum([...]), z.string()]) patterns
 * while also allowing custom values not in the list.
 *
 * Features:
 * - Autocomplete with suggestions from the enum/string list
 * - Allows custom values not in the list (shown as "xxx (custom)")
 * - Search filtering
 * - Mouse-based selection
 */
// eslint-disable-next-line complexity
const AutoCompleteEnumBuilder: React.FC<FieldProps<ZodUnion<any>>> = ({
  label,
  schema,
  value,
  optional,
  onChange,
  disabled,
}) => {
  const description = schema._def.description;
  const options = schema._def.options;
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Check if this is a union of enum and string (pattern: z.union([z.enum([...]), z.string()]))
  const enumOption = useMemo(
    () => options.find((opt: any) => opt._def.typeName === ZodFirstPartyTypeKind.ZodEnum),
    [options],
  );
  const stringOption = useMemo(
    () => options.find((opt: any) => opt._def.typeName === ZodFirstPartyTypeKind.ZodString),
    [options],
  );

  // Extract enum options from the union
  const enumOptions = useMemo(
    () => (enumOption ? (Object.values(enumOption._def.values) as string[]) : []),
    [enumOption],
  );

  const canUnset = value !== null && value !== undefined;

  // Check if current value is a custom value (not in the enum list)
  const isCustomValue = useMemo(() => {
    if (!value) return false;
    return !enumOptions.includes(value);
  }, [value, enumOptions]);

  // Filter options based on search value
  const filteredOptions = useMemo(() => {
    if (!searchValue.trim()) {
      return enumOptions;
    }
    const lowerSearch = searchValue.toLowerCase();
    return enumOptions.filter((option: string) => option.toLowerCase().includes(lowerSearch));
  }, [enumOptions, searchValue]);

  // Check if search value matches any existing option exactly
  const exactMatchExists = useMemo(() => {
    if (!searchValue.trim()) return false;
    return enumOptions.some((option: string) => option.toLowerCase() === searchValue.toLowerCase());
  }, [enumOptions, searchValue]);

  // Handle option selection
  const handleOptionSelect = useCallback(
    (option: string) => {
      onChange(option);
      setSearchValue('');
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onChange],
  );

  // Handle creating custom value
  const handleCreateCustomValue = useCallback(
    (customValue: string) => {
      if (!customValue.trim()) return;
      onChange(customValue.trim());
      setSearchValue('');
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onChange],
  );

  // Handle input changes
  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setSearchValue(inputValue);
    setIsOpen(true);
  }, []);

  // Handle input focus
  const handleInputFocus = useCallback(() => {
    if (!disabled) {
      setIsOpen(true);
    }
  }, [disabled]);

  // Handle clicking outside to close
  useEffect(function handleClickOutside() {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus input when popover opens
  useEffect(
    function focusInputOnOpen() {
      if (isOpen && inputRef.current) {
        inputRef.current.focus();
      }
    },
    [isOpen],
  );

  // Only render autocomplete UI if this is a union of enum and string
  if (!enumOption || !stringOption) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="flex gap-1 items-center">
        <Typography.Subtitle3>
          {label} {!optional && '*'}
        </Typography.Subtitle3>
        <MaybeTooltip text={description} />
        {canUnset && (
          <div
            onClick={e => {
              if (disabled) {
                return;
              }
              e.preventDefault();
              e.stopPropagation();
              onChange(undefined);
            }}
          >
            <Icon icon="ban" tooltip="unset" />
          </div>
        )}
      </div>

      <div ref={containerRef} className="relative w-full">
        <div className="relative">
          {/* Selected value display */}
          {value && !isOpen ? (
            <div
              className="flex items-center w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm cursor-pointer hover:bg-gray-50"
              style={{
                borderColor: !optional && !value ? 'red' : undefined,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
              }}
              onClick={() => {
                if (!disabled) {
                  setIsOpen(true);
                }
              }}
            >
              <span className="flex items-center gap-2">
                {value}
                {isCustomValue ? (
                  <Tag variant="neutral" className="text-xs">
                    custom
                  </Tag>
                ) : null}
              </span>
            </div>
          ) : (
            <TextField
              ref={inputRef}
              value={searchValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              placeholder={`Type to search or enter custom ${label}...`}
              className="w-full"
              error={!optional && !value}
              disabled={disabled}
            />
          )}
        </div>

        {isOpen && !disabled && (
          <div
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
            style={{ top: '100%' }}
          >
            {filteredOptions.length === 0 && !searchValue.trim() ? (
              <div className="p-3 text-sm text-gray-500">
                No options available. Type to enter a custom value.
              </div>
            ) : filteredOptions.length === 0 && searchValue.trim() ? (
              <div className="p-3 text-sm text-gray-500">No matching options found.</div>
            ) : (
              filteredOptions.map((option: string) => (
                <div
                  key={option}
                  className="cursor-pointer px-3 py-2 text-sm transition-colors flex items-center justify-between hover:bg-gray-100"
                  onClick={() => handleOptionSelect(option)}
                >
                  <span className="truncate">{option}</span>
                  {option === value && <Icon icon="check" size="sm" />}
                </div>
              ))
            )}

            {/* Show create custom option when there's a search value and it doesn't exactly match */}
            {searchValue.trim().length > 0 && !exactMatchExists && (
              <div className="border-t border-gray-200">
                <div
                  className="cursor-pointer px-3 py-2 text-sm transition-colors hover:bg-gray-100 italic"
                  onClick={() => handleCreateCustomValue(searchValue)}
                >
                  <div className="font-medium flex items-center gap-2">
                    Use "{searchValue}"
                    <Tag variant="neutral" className="text-xs">
                      custom
                    </Tag>
                  </div>
                  <div className="text-xs text-gray-500">
                    Add as custom value not in the predefined list
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoCompleteEnumBuilder;
